import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { SCREENS } from '../utils/constants';
import { useTranslation } from '../providers/TranslationProvider';

// UI Context oluştur
const UIContext = createContext();

/**
 * UI Provider - Simplified version focused on feed management
 */
export const UIProvider = ({ children, initialTheme = false }) => {
  const { t } = useTranslation();

  // Tema yönetimi
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Navigation state
  const [currentScreen, setCurrentScreen] = useState(SCREENS.WELCOME);
  const [screenParams, setScreenParams] = useState({});
  const [navigationError, setNavigationError] = useState(null);

  // Feed items state management
  const [feedItems, setFeedItems] = useState([]);

  // Touch/pull-down menu state (for AnimatedBackground/PageLayout compatibility)
  const [showPullDownMenu, setShowPullDownMenu] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [pullDownProgress, setPullDownProgress] = useState(0);
  const touchStartYRef = useRef(null);

  // Mouse position shared state (used by AnimatedBackground)
  // DISABLED globally to prevent re-renders on layout components
  // Components needing mouse position should implement their own listeners
  /*
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => setMousePosition({ x: e.clientX || 0, y: e.clientY || 0 });
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);
  */

  // Debug helper
  const logDebug = useCallback((message, ...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[UIContext] ${message}`, ...args);
    }
  }, []);

  // Theme toggle function
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('theme', newValue ? 'dark' : 'light');

      // Update body classes immediately for visual feedback
      try {
        document.body.classList.remove(newValue ? 'light-theme' : 'dark-theme');
        document.body.classList.add(newValue ? 'dark-theme' : 'light-theme');
      } catch { }

      // Notify listeners (App.jsx) about theme change
      try {
        const event = new CustomEvent('themeChanged', { detail: { isDarkMode: newValue } });
        window.dispatchEvent(event);
      } catch { }

      return newValue;
    });
  }, []);

  // Ensure DOM classes reflect current theme on mount and when changed
  useEffect(() => {
    try {
      document.body.classList.toggle('dark-theme', isDarkMode);
      document.body.classList.toggle('light-theme', !isDarkMode);
    } catch { }
  }, [isDarkMode]);

  // Add new post to feed
  const addFeedItem = useCallback((newItem) => {
    const feedItem = {
      id: Date.now(),
      timestamp: Date.now(),
      ...newItem
    };

    setFeedItems(prevItems => [feedItem, ...prevItems]);
    logDebug('Added new feed item:', feedItem);
  }, [logDebug]);

  // Remove feed item
  const removeFeedItem = useCallback((itemId) => {
    setFeedItems(prevItems => prevItems.filter(item => item.id !== itemId));
    logDebug('Removed feed item:', itemId);
  }, [logDebug]);

  // Update feed item
  const updateFeedItem = useCallback((itemId, updates) => {
    setFeedItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
    logDebug('Updated feed item:', itemId, updates);
  }, [logDebug]);

  // Clear all feed items
  const clearFeedItems = useCallback(() => {
    setFeedItems([]);
    logDebug('Cleared all feed items');
  }, [logDebug]);

  // Navigation function
  const navigateToScreen = useCallback((screen, params = {}) => {
    try {
      setNavigationError(null);
      logDebug('Navigating to screen:', screen, params);

      setCurrentScreen(screen);
      setScreenParams(params);

    } catch (error) {
      console.error('Navigation error:', error);
      setNavigationError(error.message);
    }
  }, [logDebug]);

  // Simple security verification stub
  const showSecurityVerification = useCallback((onVerified) => {
    try {
      // In absence of a real verification UI, immediately call the callback
      if (typeof onVerified === 'function') onVerified();
    } catch (e) {
      console.error('Security verification error:', e);
    }
  }, []);

  // Touch handlers (no-op safe) used by PageLayout/AnimatedBackground
  const handleTouchStart = useCallback((e) => {
    try {
      const y = e?.touches?.[0]?.clientY ?? e?.clientY ?? 0;
      touchStartYRef.current = y;
      setIsPulling(true);
      setPullDownProgress(0);
    } catch { }
  }, []);

  const handleTouchMove = useCallback((e) => {
    try {
      if (touchStartYRef.current == null) return;
      const y = e?.touches?.[0]?.clientY ?? e?.clientY ?? 0;
      const delta = Math.max(0, y - touchStartYRef.current);
      const progress = Math.min(100, Math.round((delta / 120) * 100));
      setPullDownProgress(progress);
    } catch { }
  }, []);

  const handleTouchEnd = useCallback(() => {
    try {
      if (pullDownProgress >= 100) {
        setShowPullDownMenu(true);
      }
      setIsPulling(false);
      setPullDownProgress(0);
      touchStartYRef.current = null;
    } catch { }
  }, [pullDownProgress]);

  const closePullDownMenu = useCallback(() => setShowPullDownMenu(false), []);

  // Provider değeri
  const value = {
    // Tema durumları
    isDarkMode,
    toggleTheme,

    // Navigation
    currentScreen,
    screenParams,
    navigateToScreen,
    navigationError,

    // Feed items management
    feedItems,
    addFeedItem,
    removeFeedItem,
    updateFeedItem,
    clearFeedItems,

    // Touch/pull-down interop
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isPulling,
    pullDownProgress,
    showPullDownMenu,
    closePullDownMenu,
    showSecurityVerification,
    // mousePosition removed to prevent re-renders
  };

  return (
    <UIContext.Provider value={value}>
      {children}
    </UIContext.Provider>
  );
};

// Custom hook to use the UI context
export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};
